import React, { useState, useRef, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Chart } from 'primereact/chart';
import { Chart as ChartJS } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { BarrioContext } from './BarrioContext';
import { useAuth } from './AuthContext';
import { getResumenGeneral, getGastosPorRubro, getGastosPorProveedor } from '../service/DashboardService';

const CHART_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#a855f7', '#ec4899', '#84cc16'];

// Mismo plugin de texto central que components/Dashboard.js, registrado en su propio
// namespace de Chart.js para no depender de que ese archivo se haya importado antes.
const centerTextPlugin2 = {
    id: 'centerText2',
    afterDraw(chart) {
        const opts = chart.config?.options?.plugins?.centerText2;
        if (!opts?.display) return;
        const { ctx } = chart;
        const { left, right, top, bottom } = chart.chartArea;
        const cx = (left + right) / 2;
        const cy = (top + bottom) / 2;
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = '500 11px Inter, system-ui, sans-serif';
        ctx.fillStyle = '#667085';
        ctx.fillText('Total gastos:', cx, cy - 14);
        const val = opts.value || '';
        ctx.font = `bold ${val.length > 13 ? 12 : 14}px Inter, system-ui, sans-serif`;
        ctx.fillStyle = '#101828';
        ctx.fillText(val, cx, cy + 8);
        ctx.restore();
    }
};
ChartJS.register(ChartDataLabels, centerTextPlugin2);

const formatARS = (value) =>
    new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(value ?? 0);

const fmtFechaCorta = (f) => {
    if (!f) return '';
    const d = new Date(f);
    const hoy = new Date(); const ayer = new Date(hoy); ayer.setDate(hoy.getDate() - 1);
    if (d.toDateString() === hoy.toDateString()) return 'Hoy';
    if (d.toDateString() === ayer.toDateString()) return 'Ayer';
    return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
};

const btnIconStyle = {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: '0.5rem', cursor: 'pointer', background: 'none', border: 'none'
};
const circleStyle = {
    width: 48, height: 48, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '1.2rem', color: '#344054', background: '#f2f4f7'
};
const cardStyle = {
    background: '#fff', border: '1px solid #e4e7ec',
    borderRadius: 16, padding: '1.5rem 1.5rem 1.25rem',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
};

const Dashboard2 = () => {
    const toast = useRef(null);
    const navigate = useNavigate();
    const HOST_API = process.env.REACT_APP_URL;
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();

    const _user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
    const usuarioId = _user.id || _user.usuarioId || 0;

    const [selectedDate, setSelectedDate] = useState(new Date());

    // ── Comunicados / Reclamos ──
    const [comunicadosData, setComunicadosData] = useState([]);
    const [reclamosDash, setReclamosDash] = useState([]);

    useEffect(() => {
        if (!barrio?.id) return;
        axios.get(`${HOST_API}api/comunicados/GetComunicados?administracionId=${administracionId}&barrioId=${barrio.id}`)
            .then(({ data }) => setComunicadosData(data ?? []))
            .catch(() => {});
    }, [barrio?.id, HOST_API, administracionId]);

    useEffect(() => {
        if (!barrio?.id || !usuarioId) return;
        axios.get(`${HOST_API}api/reclamos/GetReclamosUsuario?administracionId=${administracionId}&barrioId=${barrio.id}&usuarioId=${usuarioId}`)
            .then(({ data }) => setReclamosDash(data ?? []))
            .catch(() => {});
    }, [barrio?.id, usuarioId, HOST_API, administracionId]);

    const reclamosPendientes = useMemo(
        () => reclamosDash.filter(r => r.estado !== 'leido' && r.estado !== 'Leído').length,
        [reclamosDash]
    );

    // ── Estado de cuenta (saldo del mes) ──
    const [ufsUsuario, setUfsUsuario] = useState([]);
    const [ufSeleccionada, setUfSeleccionada] = useState(null);
    const [historialCuenta, setHistorialCuenta] = useState([]);

    useEffect(() => {
        if (!barrio?.id || !usuarioId) return;
        axios.get(`${HOST_API}api/unidadesFuncionales/GetUFsByUsuario?usuarioId=${usuarioId}&barrioId=${barrio.id}`)
            .then(({ data }) => {
                setUfsUsuario(data ?? []);
                setUfSeleccionada((prev) => prev ?? (data?.[0]?.unidadFuncionalId ?? null));
            })
            .catch(() => {});
    }, [barrio?.id, usuarioId, HOST_API]);

    useEffect(() => {
        if (!barrio?.id || !ufSeleccionada) { setHistorialCuenta([]); return; }
        axios.get(`${HOST_API}api/estadoCuentas/GetEstadoCuenta`, {
            params: { barrioId: barrio.id, unidadFuncionalId: ufSeleccionada, administracionId }
        })
            .then(({ data }) => setHistorialCuenta(data ?? []))
            .catch(() => setHistorialCuenta([]));
    }, [barrio?.id, ufSeleccionada, administracionId, HOST_API]);

    const estadoCuentaMes = useMemo(() => {
        const d = selectedDate || new Date();
        return historialCuenta.find((r) => {
            const rd = new Date(r.periodo);
            return rd.getFullYear() === d.getFullYear() && rd.getMonth() === d.getMonth();
        }) ?? null;
    }, [historialCuenta, selectedDate]);

    // ── Medios de pago ──
    const [mediosPagoVisible, setMediosPagoVisible] = useState(false);
    const copiarAlPortapapeles = (valor, etiqueta) => {
        if (!valor) return;
        navigator.clipboard.writeText(valor);
        toast.current?.show({ severity: 'success', summary: 'Copiado', detail: `${etiqueta} copiado al portapapeles`, life: 2500 });
    };

    // ── Gastos del consorcio (sección secundaria, colapsable) ──
    const [gastosAbierto, setGastosAbierto] = useState(false);
    const [resumen, setResumen] = useState(null);
    const [gastosPorRubro, setGastosPorRubro] = useState([]);
    const [gastosPorProveedor, setGastosPorProveedor] = useState([]);
    const [loadingGastos, setLoadingGastos] = useState(false);

    useEffect(() => {
        if (!barrio?.id || !gastosAbierto) return;
        const d = selectedDate || new Date();
        const periodo = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

        setLoadingGastos(true);
        Promise.all([
            getResumenGeneral(barrio.id, administracionId, periodo),
            getGastosPorRubro(barrio.id, administracionId, periodo),
            getGastosPorProveedor(barrio.id, administracionId, periodo),
        ])
            .then(([resumenRes, rubrosRes, proveedoresRes]) => {
                setResumen(resumenRes.data);
                setGastosPorRubro(rubrosRes.data ?? []);
                setGastosPorProveedor(proveedoresRes.data ?? []);
            })
            .catch(() => {})
            .finally(() => setLoadingGastos(false));
    }, [barrio?.id, administracionId, selectedDate, gastosAbierto]);

    const chartRubros = useMemo(() => ({
        labels: gastosPorRubro.map(g => g.grupoGasto),
        datasets: [{
            data: gastosPorRubro.map(g => g.totalRubro),
            backgroundColor: CHART_COLORS.slice(0, gastosPorRubro.length),
            borderWidth: 2,
        }]
    }), [gastosPorRubro]);

    const optionsRubros = useMemo(() => {
        const totalGastos = gastosPorRubro.reduce((sum, g) => sum + g.totalRubro, 0);
        return {
            cutout: '65%',
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true, position: 'bottom',
                    labels: {
                        boxWidth: 12, padding: 14, font: { size: 11 },
                        generateLabels: (chart) =>
                            chart.data.labels.map((label, i) => ({
                                text: label.length > 28 ? label.slice(0, 28) + '…' : label,
                                fillStyle: CHART_COLORS[i % CHART_COLORS.length],
                                strokeStyle: CHART_COLORS[i % CHART_COLORS.length],
                                hidden: false, index: i,
                            })),
                    }
                },
                tooltip: {
                    callbacks: {
                        label: (ctx) => {
                            const item = gastosPorRubro[ctx.dataIndex];
                            return [` ${formatARS(item.totalRubro)}`, ` ${item.porcentaje.toFixed(1)}%`, ` ${item.cantidadGastos} gasto(s)`];
                        }
                    }
                },
                datalabels: {
                    display: (ctx) => {
                        const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                        return total > 0 && (ctx.dataset.data[ctx.dataIndex] / total) * 100 >= 5;
                    },
                    color: '#fff', anchor: 'center', align: 'center',
                    font: { weight: 'bold', size: 12 },
                    formatter: (value, ctx) => {
                        const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                        return `${((value / total) * 100).toFixed(1)}%`;
                    },
                    textShadowColor: 'rgba(0,0,0,0.35)', textShadowBlur: 4,
                },
                centerText2: { display: true, value: formatARS(totalGastos) },
            }
        };
    }, [gastosPorRubro]);

    const proveedoresOrdenados = useMemo(() =>
        [...gastosPorProveedor].sort((a, b) => b.totalProveedor - a.totalProveedor),
    [gastosPorProveedor]);

    const chartProveedores = useMemo(() => ({
        labels: proveedoresOrdenados.map(p => p.nombreProveedor),
        datasets: [{
            label: 'Total',
            data: proveedoresOrdenados.map(p => p.totalProveedor),
            backgroundColor: proveedoresOrdenados.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
            borderRadius: 4,
        }]
    }), [proveedoresOrdenados]);

    const optionsProveedores = useMemo(() => ({
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const item = proveedoresOrdenados[ctx.dataIndex];
                        return [` ${formatARS(item.totalProveedor)}`, ` ${item.cantidadGastos} gasto(s)`];
                    }
                }
            },
            datalabels: { display: false }
        },
        scales: {
            x: { ticks: { callback: (val) => `$${(val / 1000).toFixed(0)}k` }, grid: { color: '#e5e7eb' } },
            y: { ticks: { font: { size: 11 } }, grid: { display: false } }
        }
    }), [proveedoresOrdenados]);

    // ── Reservas: lista compacta de próximas (en vez de calendario completo) ──
    const [reservasDash, setReservasDash] = useState([]);

    useEffect(() => {
        if (!barrio?.id) return;
        axios.get(`${HOST_API}api/reservasSUM/GetReservas?administracionId=${administracionId}&barrioId=${barrio.id}`)
            .then(({ data }) => setReservasDash(data ?? []))
            .catch(() => {});
    }, [barrio?.id, HOST_API, administracionId]);

    const proximasReservas = useMemo(() => {
        const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
        return reservasDash
            .filter(r => r.estado !== 'Cancelada' && new Date(r.fecha) >= hoy)
            .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
            .slice(0, 5);
    }, [reservasDash]);

    // ── Render ───────────────────────────────────────────────────────────────────
    const periodoLabel = selectedDate ? selectedDate.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' }) : '';
    const saldo = estadoCuentaMes?.saldoFinal ?? 0;
    const alDia = saldo <= 0;

    return (
        <div>
            <Toast ref={toast} />

            {/* ── Dialog Medios de pago ── */}
            <Dialog header="Medios de pago" visible={mediosPagoVisible} style={{ width: '90vw', maxWidth: '420px' }} onHide={() => setMediosPagoVisible(false)}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {[
                        { label: `${barrio?.cuentaBancaria || 'Banco'} - ${barrio?.tipoCuentaBancaria || 'CBU'}`, valor: barrio?.numeroCuenta },
                        { label: `${barrio?.cuentaBancaria || 'Banco'} - Alias`, valor: barrio?.alias },
                    ].filter((row) => row.valor).map((row, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.9rem 0', borderBottom: '1px solid #eaecf0' }}>
                            <div>
                                <div style={{ fontWeight: 600, color: '#101828', fontSize: '0.9rem' }}>{row.label}</div>
                                <div style={{ color: '#667085', fontSize: '0.85rem', marginTop: '0.15rem' }}>{row.valor}</div>
                            </div>
                            <Button icon="pi pi-copy" className="p-button-text p-button-rounded" onClick={() => copiarAlPortapapeles(row.valor, row.label)} />
                        </div>
                    ))}
                    {!barrio?.numeroCuenta && !barrio?.alias && (
                        <p style={{ color: '#98a2b3', fontSize: '0.85rem', textAlign: 'center', margin: '1rem 0' }}>Todavía no se cargaron los datos bancarios del barrio.</p>
                    )}
                </div>
            </Dialog>

            <div className="flex justify-content-between align-items-center flex-wrap gap-2">
                <h4 style={{ margin: 0 }}>Dashboard</h4>
                <Calendar value={selectedDate} view="month" dateFormat="mm/yy" onChange={(e) => setSelectedDate(e.value)} showIcon className="p-inputtext-sm" />
            </div>

            {/* ── 1. Hero: saldo + accesos rápidos ── */}
            <div style={{ marginTop: '1.2rem', marginBottom: '1.5rem' }}>
                <div style={{
                    background: 'linear-gradient(135deg, #4f8ff7, #0a3d91)',
                    borderRadius: 24, padding: '1.25rem 1.25rem 2.5rem',
                    boxShadow: '0 14px 30px -12px rgba(10,61,145,0.4)',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>Saldo Expensas</span>
                        <span style={{ background: 'rgba(255,255,255,0.18)', borderRadius: 99, padding: '0.25rem 0.65rem', fontSize: '0.72rem', fontWeight: 700, color: '#fff' }}>
                            {alDia ? '✓ Al día' : '⚠ Pendiente'}
                        </span>
                    </div>
                    {ufsUsuario.length > 1 && (
                        <Dropdown value={ufSeleccionada} options={ufsUsuario} optionLabel="unidadFuncional" optionValue="unidadFuncionalId"
                            onChange={(e) => setUfSeleccionada(e.value)} className="p-inputtext-sm" style={{ marginTop: '0.6rem', width: '100%' }} />
                    )}
                    <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.65)', marginTop: '0.6rem', marginBottom: 0, textTransform: 'capitalize' }}>{periodoLabel}</p>
                    <p style={{ fontSize: '2rem', fontWeight: 800, color: '#fff', margin: '0.2rem 0 0' }}>{formatARS(saldo)}</p>
                </div>
                <div style={{
                    display: 'flex', justifyContent: 'space-around', background: '#fff', borderRadius: 20,
                    marginTop: -24, marginLeft: 16, marginRight: 16, paddingTop: '1rem', paddingBottom: '0.5rem',
                    boxShadow: '0 6px 16px -8px rgba(16,24,40,0.15)',
                }}>
                    <button style={btnIconStyle} onClick={() => navigate('/subirComprobante')}>
                        <div style={circleStyle}><i className="pi pi-credit-card" /></div>
                        <span style={{ fontSize: '0.72rem', color: '#344054', textAlign: 'center' }}>Informar<br />pago</span>
                    </button>
                    <button style={btnIconStyle} onClick={() => navigate('/estadoCuenta')}>
                        <div style={circleStyle}><i className="pi pi-file" /></div>
                        <span style={{ fontSize: '0.72rem', color: '#344054', textAlign: 'center' }}>Estado<br />de cuenta</span>
                    </button>
                    <button style={btnIconStyle} onClick={() => setMediosPagoVisible(true)}>
                        <div style={circleStyle}><i className="pi pi-wallet" /></div>
                        <span style={{ fontSize: '0.72rem', color: '#344054', textAlign: 'center' }}>Medios<br />de pago</span>
                    </button>
                    <button style={btnIconStyle} onClick={() => setGastosAbierto(true)}>
                        <div style={circleStyle}><i className="pi pi-chart-pie" /></div>
                        <span style={{ fontSize: '0.72rem', color: '#344054', textAlign: 'center' }}>Gastos<br />consorcio</span>
                    </button>
                </div>
            </div>

            {/* ── 2. Novedades: Comunicados + Reclamos en un solo card con tabs ── */}
            <div style={{ ...cardStyle, marginBottom: '1.5rem' }}>
                <TabView>
                    <TabPanel header={`Comunicados${comunicadosData.length ? ` (${comunicadosData.length})` : ''}`}>
                        <div className="flex justify-content-end mb-2">
                            <button onClick={() => navigate('/comunicadosBarrio')} style={{ background: 'none', border: 'none', color: '#1570ef', fontWeight: 500, fontSize: '0.85rem', cursor: 'pointer', padding: 0 }}>
                                Ver todos →
                            </button>
                        </div>
                        {comunicadosData.length === 0 ? (
                            <p style={{ color: '#98a2b3', fontSize: '0.85rem', textAlign: 'center', margin: '1rem 0' }}>Sin comunicados recientes</p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                                {comunicadosData.slice(0, 4).map((c) => (
                                    <div key={c.comunicadoId} style={{
                                        background: '#fff', border: `1px solid ${c.urgente ? '#fecdca' : '#e4e7ec'}`,
                                        borderLeft: `4px solid ${c.urgente ? '#f04438' : '#1570ef'}`, borderRadius: 10,
                                        padding: '0.75rem 0.9rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.3rem' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flex: 1, minWidth: 0 }}>
                                                {c.urgente && (
                                                    <span style={{ background: '#fef3f2', color: '#f04438', fontWeight: 700, fontSize: '0.65rem', padding: '0.1rem 0.4rem', borderRadius: 99, border: '1px solid #fecdca', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                        URGENTE
                                                    </span>
                                                )}
                                                <span style={{ fontWeight: 600, color: '#101828', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.asunto}</span>
                                            </div>
                                            <span style={{ fontSize: '0.75rem', color: '#98a2b3', whiteSpace: 'nowrap', flexShrink: 0 }}>{fmtFechaCorta(c.fecha)}</span>
                                        </div>
                                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#667085', lineHeight: 1.45, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{c.comunicado}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabPanel>
                    <TabPanel header={`Reclamos${reclamosPendientes ? ` (${reclamosPendientes})` : ''}`}>
                        <div className="flex justify-content-end mb-2">
                            <button onClick={() => navigate('/reclamos')} style={{ background: 'none', border: 'none', color: '#1570ef', fontWeight: 500, fontSize: '0.85rem', cursor: 'pointer', padding: 0 }}>
                                Ver todos →
                            </button>
                        </div>
                        {reclamosDash.length === 0 ? (
                            <p style={{ color: '#98a2b3', fontSize: '0.85rem', textAlign: 'center', margin: '1rem 0' }}>Sin reclamos recientes</p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                                {reclamosDash.slice(0, 4).map((r) => {
                                    const leido = r.estado === 'leido' || r.estado === 'Leído';
                                    return (
                                        <div key={r.reclamoId} style={{
                                            background: '#fff', border: `1px solid ${leido ? '#e4e7ec' : '#fef0c7'}`,
                                            borderLeft: `4px solid ${leido ? '#12b76a' : '#f79009'}`, borderRadius: 10,
                                            padding: '0.75rem 0.9rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.3rem' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flex: 1, minWidth: 0 }}>
                                                    <span style={{
                                                        background: leido ? '#ecfdf3' : '#fffaeb', color: leido ? '#12b76a' : '#b54708',
                                                        border: `1px solid ${leido ? '#a9efc5' : '#fedf89'}`, fontWeight: 700, fontSize: '0.65rem',
                                                        padding: '0.1rem 0.4rem', borderRadius: 99, whiteSpace: 'nowrap', flexShrink: 0,
                                                    }}>
                                                        {leido ? 'LEÍDO' : 'PENDIENTE'}
                                                    </span>
                                                    <span style={{ fontWeight: 600, color: '#101828', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.tipoReclamoNombre || r.tipoNombre || 'Reclamo'}</span>
                                                </div>
                                                <span style={{ fontSize: '0.75rem', color: '#98a2b3', whiteSpace: 'nowrap', flexShrink: 0 }}>{fmtFechaCorta(r.fecha)}</span>
                                            </div>
                                            {r.descripcion && (
                                                <p style={{ margin: 0, fontSize: '0.8rem', color: '#667085', lineHeight: 1.45, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{r.descripcion}</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </TabPanel>
                </TabView>
            </div>

            {/* ── 3. Próximas reservas: lista compacta, no calendario completo ── */}
            <div style={{ ...cardStyle, marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <span style={{ fontWeight: 600, color: '#101828', fontSize: '0.95rem' }}>Próximas reservas</span>
                    <button onClick={() => navigate('/reservarSum')} style={{ background: 'none', border: 'none', color: '#1570ef', fontWeight: 500, fontSize: '0.85rem', cursor: 'pointer', padding: 0 }}>
                        Ver todas →
                    </button>
                </div>
                {proximasReservas.length === 0 ? (
                    <p style={{ color: '#98a2b3', fontSize: '0.85rem', textAlign: 'center', margin: '1rem 0' }}>No hay reservas próximas</p>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                        {proximasReservas.map((r) => (
                            <div key={r.reservaId} style={{
                                display: 'flex', alignItems: 'center', gap: '0.9rem',
                                border: '1px solid #e4e7ec', borderRadius: 10, padding: '0.65rem 0.9rem',
                            }}>
                                <div style={{
                                    width: 42, height: 42, borderRadius: 10, background: '#eff6ff',
                                    color: '#1570ef', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.1, flexShrink: 0,
                                }}>
                                    <span>{new Date(r.fecha).toLocaleDateString('es-AR', { day: '2-digit' })}</span>
                                    <span style={{ textTransform: 'uppercase' }}>{new Date(r.fecha).toLocaleDateString('es-AR', { month: 'short' })}</span>
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, color: '#101828', fontSize: '0.87rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.salonNombre}</div>
                                    <div style={{ color: '#667085', fontSize: '0.78rem' }}>
                                        {r.horaInicio ? `${r.horaInicio} - ${r.horaFin || ''}` : 'Todo el día'}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ── 4. Gastos del consorcio: secundario, colapsado por defecto ── */}
            <div style={cardStyle}>
                <button
                    onClick={() => setGastosAbierto(v => !v)}
                    style={{ background: 'none', border: 'none', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', padding: 0 }}
                >
                    <div style={{ textAlign: 'left' }}>
                        <span style={{ fontWeight: 600, color: '#101828', fontSize: '0.95rem' }}>Gastos del consorcio</span>
                        <p style={{ margin: '0.2rem 0 0', color: '#667085', fontSize: '0.82rem' }}>Detalle de gastos del período, por grupo y por proveedor</p>
                    </div>
                    <i className={`pi ${gastosAbierto ? 'pi-chevron-up' : 'pi-chevron-down'}`} style={{ color: '#667085' }} />
                </button>

                {gastosAbierto && (
                    loadingGastos ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                            <ProgressSpinner style={{ width: 40, height: 40 }} strokeWidth="4" />
                        </div>
                    ) : (
                        <div className="grid mt-2">
                            {resumen && (
                                <>
                                    <div className="col-12 md:col-6">
                                        <div className="card">
                                            <span className="block text-500 font-medium mb-2">Total general de gastos</span>
                                            <div className="text-900 font-semibold text-3xl">{formatARS(resumen.totalGeneral)}</div>
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="card">
                                            <span className="block text-500 font-medium mb-2">Cantidad de gastos</span>
                                            <div className="text-900 font-semibold text-3xl">{resumen.cantidadGastos}</div>
                                        </div>
                                    </div>
                                </>
                            )}
                            {gastosPorRubro.length > 0 && (
                                <div className="col-12 md:col-4">
                                    <div className="card" style={{ height: '100%' }}>
                                        <h5 className="mt-0">Gastos por grupo</h5>
                                        <Chart type="doughnut" data={chartRubros} options={optionsRubros} style={{ height: '320px' }} />
                                    </div>
                                </div>
                            )}
                            {gastosPorProveedor.length > 0 && (
                                <div className="col-12 md:col-8">
                                    <div className="card" style={{ height: '100%', overflow: 'hidden' }}>
                                        <h5 className="mt-0">Gastos por proveedor</h5>
                                        <Chart type="bar" data={chartProveedores} options={optionsProveedores} style={{ height: `${Math.max(220, proveedoresOrdenados.length * 45)}px` }} />
                                    </div>
                                </div>
                            )}
                            {!resumen && gastosPorRubro.length === 0 && !loadingGastos && (
                                <div className="col-12">
                                    <p style={{ color: '#98a2b3', fontSize: '0.85rem', textAlign: 'center', margin: '1rem 0' }}>Sin gastos registrados en este período</p>
                                </div>
                            )}
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default Dashboard2;
